import { uniqueSortedStrings } from '@/utils/arrays';
import { MediaPack } from '@calm/evexia-web-sdk';
import createMediaApi from '@rhg/libs/api/create-media-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    checkQueryCache,
    ItemToQueryMapping,
    queryClient,
    queryKeys,
    useQuery,
} from '@rhg/libs/react-query';
import { getRhgAppHeader } from '@rhg/utils/browser';
import { useActiveCircleId } from '../use-active-circle';
import { useCircle } from '../use-circle';

export const packsQueryOptions = {
    /**
     * The key should be a pack class map for easy invalidation
     * This allows us to invalidate any query with a given pack class by passing in [queryKeys.packsByPackClass, circleId, { somePackClass: 'somePackClass' }]
     *
     * Refs:
     * https://tanstack.com/query/latest/docs/framework/react/guides/query-invalidation#query-matching-with-invalidatequeries
     * https://tanstack.com/query/latest/docs/framework/react/guides/query-keys#array-keys-with-variables
     */
    queryKey: function ({
        packClasses,
        circleId,
    }: {
        packClasses?: string[];
        circleId: number;
    }) {
        return [
            queryKeys.packsByPackClass,
            circleId,
            packClasses
                ? packClasses.reduce(
                      (packClassMap, packClass) => ({
                          ...packClassMap,
                          [packClass]: packClass,
                      }),
                      {}
                  )
                : {},
        ];
    },
    queryFn: async function ({
        getAccessTokenSilently,
        packClasses,
        circleId,
        circleAuthorizationToken,
        rHGApp,
    }: {
        getAccessTokenSilently: () => Promise<string>;
        packClasses: string[];
        circleId: number;
        circleAuthorizationToken: string;
        rHGApp: string;
    }) {
        const mediaApi = createMediaApi(getAccessTokenSilently);

        const response = await mediaApi.getPacksByClass({
            circleAuthorizationToken,
            recipientId: circleId,
            _class: uniqueSortedStrings(packClasses),
            xDevicePlatform: 'www',
            rHGApp,
        });

        return response.data;
    },
    queryOptions: function ({
        packClasses,
        isAuthenticated,
        circleId,
        circleAuthorizationToken,
    }: {
        packClasses: string[];
        isAuthenticated: boolean;
        circleId: number;
        circleAuthorizationToken: string;
    }) {
        return {
            enabled: !!(
                isAuthenticated &&
                circleId > 0 &&
                circleAuthorizationToken &&
                packClasses?.length
            ),
            staleTime: 1000 * 60 * 15, // 15 minutes
        };
    },
};

export function usePacks(
    packClasses: string[] = [],
    circleId: number,
    isOrdered = false
) {
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { circle } = useCircle(circleId);
    const circleAuthorizationToken = circle?.authorizationJWT ?? '';
    const { queryKey, queryFn, queryOptions } = packsQueryOptions;
    const rHGApp = getRhgAppHeader();

    const options = queryOptions({
        packClasses,
        isAuthenticated,
        circleId,
        circleAuthorizationToken,
    });

    const { data, ...otherProps } = useQuery({
        queryKey: queryKey({ packClasses, circleId }),
        queryFn: async () => {
            if (!packClasses.length) return [];

            const keyObjects = packClasses.map(
                (packClass): ItemToQueryMapping<string> => ({
                    item: packClass,
                    queryKey: queryKey({ packClasses: [packClass], circleId }),
                })
            );

            const { cachedQueries, invalidQueries } = checkQueryCache<
                string,
                MediaPack[]
            >(keyObjects, options.staleTime);
            const cachedQueriesData = cachedQueries.reduce<MediaPack[]>(
                (cachedData, item) => [...cachedData, ...item.data],
                []
            );

            // If all packs are already fetched and not stale, return them
            if (invalidQueries.length === 0) {
                return cachedQueriesData;
            }

            const packClassesToFetch = invalidQueries.map(({ item }) => item);

            // Fetch the packs we need
            const response = await queryFn({
                getAccessTokenSilently,
                packClasses: packClassesToFetch,
                circleId,
                circleAuthorizationToken,
                rHGApp,
            });

            // populate cache for each pack class
            response?.forEach((pack) => {
                const key = queryKey({
                    packClasses: [pack.packClass],
                    circleId,
                });
                queryClient.setQueryData<MediaPack[]>(key, [pack]);
            });

            // Combine newly fetched packs with existing packs
            return [...cachedQueriesData, ...(response || [])];
        },
        ...options,
    });

    // Order packs
    const packs = isOrdered
        ? packClasses.reduce<MediaPack[]>((orderedPacks, packClass) => {
              const pack = data?.find((pack) => pack.packClass === packClass);
              if (pack) {
                  return [...orderedPacks, pack];
              } else {
                  return orderedPacks;
              }
          }, [])
        : data;

    return {
        packs,
        isEnabled: options.enabled,
        ...otherProps,
    };
}

export function useActiveCirclePacks(
    packClasses: string[] = [],
    isOrdered = false
) {
    const { activeCircleId } = useActiveCircleId();
    return usePacks(packClasses, activeCircleId, isOrdered);
}
